import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Calendar, AlertTriangle, CheckCircle, Clock, UserCheck, X, Bell, Mail, MessageSquare } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const STATUS_FLOW = {
  waiting: { next: "in_consultation", label: "Check In", color: "bg-blue-600 hover:bg-blue-700" },
  in_consultation: { next: "completed", label: "Complete", color: "bg-emerald-600 hover:bg-emerald-700" },
  completed: { next: null, label: "Done", color: "bg-slate-300" },
};

const STATUS_STYLES = {
  waiting: "bg-amber-100 text-amber-800",
  in_consultation: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
};

export default function Appointments() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [filterStatus, setFilterStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", visit_date: new Date().toISOString().split("T")[0], complaint: "", is_emergency: false, visit_type: "OPD" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedDate) params.append("date", selectedDate);
      if (filterStatus) params.append("status", filterStatus);
      const { data } = await api.get(`/appointments?${params}`);
      setAppointments(data.data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, selectedDate, toast]);

  useEffect(() => {
    fetchData();
    Promise.all([api.get("/patients"), api.get("/doctors")]).then(([p, d]) => {
      setPatients(p.data); setDoctors(d.data);
    });
  }, [fetchData]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}/status`, { status });
      fetchData();
      toast({ title: `Status updated to ${status}` });
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/appointments", form);
      toast({ title: "Appointment booked successfully" });
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this appointment?")) return;
    try {
      await api.put(`/appointments/${id}/status`, { status: "cancelled" });
      toast({ title: "Appointment cancelled" });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const sendReminder = async (id, type) => {
    try {
      const { data } = await api.post(`/appointments/${id}/remind`, { type });
      toast({ title: "Reminder sent (demo)", description: data.message });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const counts = {
    waiting: appointments.filter(a => a.status === "waiting").length,
    in_consultation: appointments.filter(a => a.status === "in_consultation").length,
    completed: appointments.filter(a => a.status === "completed").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("appointments")}</h1>
          <p className="text-slate-500 text-sm mt-1">Manage patient appointments and queue</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={() => setShowModal(true)} data-testid="add-appointment-btn">
          <Plus size={16} /> Book Appointment
        </Button>
      </div>

      {/* Queue Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Waiting", count: counts.waiting, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "In Consultation", count: counts.in_consultation, icon: UserCheck, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Completed", count: counts.completed, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
        ].map(({ label, count, icon: Icon, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-4 flex items-center gap-3 cursor-pointer border border-transparent hover:border-slate-200 transition-colors`} onClick={() => setFilterStatus(filterStatus === label.toLowerCase().replace(" ", "_") ? "" : label.toLowerCase().replace(" ", "_"))}>
            <Icon size={22} className={color} />
            <div>
              <p className={`text-2xl font-bold font-sans ${color}`}>{count}</p>
              <p className="text-xs text-slate-600">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-3">
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wide">Date</Label>
            <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="mt-1 border-slate-200 w-44" data-testid="date-filter-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 uppercase tracking-wide">Status</Label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="status-filter-select">
              <option value="">All Status</option>
              <option value="waiting">Waiting</option>
              <option value="in_consultation">In Consultation</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="self-end">
            <Button variant="outline" size="sm" onClick={() => { setSelectedDate(new Date().toISOString().split("T")[0]); setFilterStatus(""); }}>
              Today
            </Button>
          </div>
        </div>
      </div>

      {/* Queue Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          <p className="text-sm font-semibold text-slate-700">{appointments.length} appointments</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-700">Token</TableHead>
              <TableHead className="font-semibold text-slate-700">Patient</TableHead>
              <TableHead className="font-semibold text-slate-700">Doctor</TableHead>
              <TableHead className="font-semibold text-slate-700">Type</TableHead>
              <TableHead className="font-semibold text-slate-700">Status</TableHead>
              <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : appointments.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                <Calendar size={32} className="mx-auto mb-2 opacity-30" />
                <p>No appointments found</p>
              </TableCell></TableRow>
            ) : appointments.map(appt => (
              <TableRow key={appt.id} className={`table-row-hover ${appt.is_emergency ? "bg-rose-50/50" : ""}`} data-testid={`appt-row-${appt.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center border border-emerald-100">
                      <span className="text-sm font-bold text-emerald-700">#{appt.token_number}</span>
                    </div>
                    {appt.is_emergency && <AlertTriangle size={14} className="text-rose-500" />}
                  </div>
                </TableCell>
                <TableCell>
                  <p className="font-medium text-slate-900 text-sm">{appt.patient_name}</p>
                  {appt.complaint && <p className="text-xs text-slate-500 truncate max-w-36">{appt.complaint}</p>}
                </TableCell>
                <TableCell className="text-sm text-slate-700">{formatDoctorName(appt.doctor_name)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">{appt.visit_type}</Badge>
                </TableCell>
                <TableCell>
                  <Badge className={`text-xs ${STATUS_STYLES[appt.status] || "bg-slate-100 text-slate-700"}`}>
                    {appt.status?.replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {STATUS_FLOW[appt.status]?.next && (
                      <Button size="sm" className={`h-7 text-xs text-white ${STATUS_FLOW[appt.status].color}`} onClick={() => updateStatus(appt.id, STATUS_FLOW[appt.status].next)} data-testid={`status-btn-${appt.id}`}>
                        {STATUS_FLOW[appt.status].label}
                      </Button>
                    )}
                    {appt.status !== "completed" && appt.status !== "cancelled" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${appt.reminder_sent ? "text-emerald-600" : "text-slate-400 hover:text-amber-600"}`} data-testid={`remind-appt-${appt.id}`} title={appt.reminder_sent ? `Reminder sent via ${appt.reminder_type}` : "Send reminder"}>
                            <Bell size={14} className={appt.reminder_sent ? "fill-emerald-500" : ""} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => sendReminder(appt.id, "email")} data-testid={`remind-email-${appt.id}`}>
                            <Mail size={14} className="mr-2 text-blue-600" /> Email Reminder
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => sendReminder(appt.id, "sms")} data-testid={`remind-sms-${appt.id}`}>
                            <MessageSquare size={14} className="mr-2 text-emerald-600" /> SMS Reminder
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {appt.status !== "completed" && appt.status !== "cancelled" && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" onClick={() => handleCancel(appt.id)} data-testid={`cancel-appt-${appt.id}`}>
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create Appointment Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans">Book New Appointment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-slate-700">Patient</Label>
              <select value={form.patient_id} onChange={e => setForm({...form, patient_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" required data-testid="appt-patient-select">
                <option value="">Select Patient</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.name} ({p.patient_id})</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Doctor</Label>
              <select value={form.doctor_id} onChange={e => setForm({...form, doctor_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" required data-testid="appt-doctor-select">
                <option value="">Select Doctor</option>
                {doctors.map(d => <option key={d.id} value={d.id}>{formatDoctorName(d.name)} — {d.specialization}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Visit Date</Label>
              <Input type="datetime-local" value={form.visit_date} onChange={e => setForm({...form, visit_date: e.target.value})} className="mt-1 border-slate-200" required data-testid="appt-date-input" />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Visit Type</Label>
              <select value={form.visit_type} onChange={e => setForm({...form, visit_type: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="appt-type-select">
                <option value="OPD">OPD</option>
                <option value="Emergency">Emergency</option>
                <option value="Follow-up">Follow-up</option>
              </select>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Complaint</Label>
              <Input value={form.complaint} onChange={e => setForm({...form, complaint: e.target.value})} placeholder="Chief complaint" className="mt-1 border-slate-200" data-testid="appt-complaint-input" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="emergency" checked={form.is_emergency} onChange={e => setForm({...form, is_emergency: e.target.checked})} className="rounded" data-testid="appt-emergency-check" />
              <Label htmlFor="emergency" className="text-sm text-rose-600 font-medium cursor-pointer">Mark as Emergency</Label>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-appt-btn">
                {saving ? "Booking..." : "Book Appointment"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
